// Callout appearance: a fixed set of safe swatches (hue + saturation only) so a
// chosen colour can never come out unreadable, with the dark-mode version
// derived from the same hue rather than configured separately.

export const CALLOUT_PALETTES = {
  blue: { label: "Blue", h: 217, s: 91 },
  indigo: { label: "Indigo", h: 243, s: 75 },
  violet: { label: "Violet", h: 272, s: 60 },
  pink: { label: "Pink", h: 330, s: 75 },
  red: { label: "Red", h: 0, s: 72 },
  orange: { label: "Orange", h: 24, s: 88 },
  amber: { label: "Amber", h: 38, s: 92 },
  lime: { label: "Lime", h: 84, s: 70 },
  green: { label: "Green", h: 158, s: 64 },
  emerald: { label: "Emerald", h: 142, s: 71 },
  teal: { label: "Teal", h: 184, s: 70 },
  cyan: { label: "Cyan", h: 199, s: 80 },
  slate: { label: "Slate", h: 215, s: 20 },
};

export const CALLOUT_ICONS = {
  info: { label: "Info", char: "i", italic: true },
  star: { label: "Star", char: "\u2605" },
  check: { label: "Check", char: "\u2713" },
  tick: { label: "Tick (bold)", char: "\u2714" },
  cross: { label: "Cross", char: "\u2716" },
  bang: { label: "Exclamation", char: "!" },
  warn: { label: "Warning", char: "\u26A0" },
  flag: { label: "Flag", char: "\u2691" },
  diamond: { label: "Diamond", char: "\u25C6" },
  list: { label: "Lines", char: "\u2261" },
  bulb: { label: "Idea", char: "\u2726" },
  clock: { label: "Clock", char: "\u25F7" },
  lock: { label: "Lock", char: "\u26BF" },
  quote: { label: "Quote", char: "\u201C" },
  play: { label: "Play", char: "\u25B6" },
  none: { label: "No icon", char: "" },
};

export const RADIUS_OPTIONS = {
  none: { label: "Square", css: "0" },
  sm: { label: "Slight", css: "0.25rem" },
  md: { label: "Rounded", css: "0.65rem" },
  lg: { label: "Very rounded", css: "1rem" },
  pill: { label: "Pill", css: "1.5rem" },
};

export const ACCENT_OPTIONS = {
  left: { label: "Left bar" },
  top: { label: "Top bar" },
  full: { label: "Full border" },
  none: { label: "No border" },
};

export const CALLOUT_VARIANT_META = [
  ["note", "Note", "blue", "info"],
  ["important", "Important", "indigo", "star"],
  ["tip", "Tip", "green", "check"],
  ["best-practice", "Best practice", "teal", "flag"],
  ["prerequisite", "Prerequisite", "violet", "diamond"],
  ["example", "Example", "slate", "list"],
  ["success", "Success", "emerald", "tick"],
  ["warning", "Warning", "amber", "bang"],
  ["danger", "Danger", "red", "warn"],
  ["do", "Do", "green", "tick"],
  ["dont", "Don't", "red", "cross"],
];

export const HEADING_SIZES = {
  sm: { label: "Small", css: "0.78rem" },
  md: { label: "Medium", css: "0.86rem" },
  lg: { label: "Large", css: "1rem" },
};
export const HEADING_WEIGHTS = {
  500: { label: "Regular" }, 600: { label: "Semibold" },
  700: { label: "Bold" }, 800: { label: "Heavy" },
};
export const HEADING_CASES = {
  none: { label: "As typed", css: "none" },
  upper: { label: "UPPERCASE", css: "uppercase" },
  title: { label: "Capitalised", css: "capitalize" },
};
export const HEADING_COLOURS = {
  match: { label: "Match the callout" },
  text: { label: "Plain text" },
};
export const DEFAULT_HEADING = { show: true, size: "md", weight: "700", case: "none", colour: "match" };

export const DEFAULT_CALLOUT_STYLES = {
  shape: { radius: "md", accent: "left", accent_width: 3, icons: true },
  variants: Object.fromEntries(
    CALLOUT_VARIANT_META.map(([id, label, palette, icon]) => [id, {
      palette, icon, title_default: label === "Don't" ? "Don't" : label,
      heading: { ...DEFAULT_HEADING },
    }]),
  ),
};

const swatch = (key) => CALLOUT_PALETTES[key] || CALLOUT_PALETTES.blue;

/** Light + dark colours for a swatch, all derived from one hue. */
export function calloutColours(key) {
  const { h, s } = swatch(key);
  return {
    light: {
      bg: `hsl(${h} ${Math.min(s, 90)}% 96.5%)`,
      accent: `hsl(${h} ${s}% ${s < 40 ? 55 : 48}%)`,
      text: `hsl(${h} ${Math.max(28, s * 0.5)}% 27%)`,
      icon: `hsl(${h} ${s}% ${s < 40 ? 48 : 42}%)`,
    },
    dark: {
      bg: `hsl(${h} ${Math.max(22, s * 0.5)}% 14%)`,
      accent: `hsl(${h} ${Math.min(s + 5, 85)}% 60%)`,
      text: `hsl(${h} ${Math.max(28, s * 0.6)}% 87%)`,
      icon: `hsl(${h} ${Math.min(s + 15, 90)}% 76%)`,
    },
  };
}

const border = (shape, colour) => {
  const w = `${Math.max(0, Math.min(Number(shape.accent_width) || 3, 8))}px`;
  if (shape.accent === "full") return `border: ${w} solid ${colour};`;
  if (shape.accent === "top") return `border: 0; border-top: ${w} solid ${colour};`;
  if (shape.accent === "none") return "border: 0;";
  return `border: 0; border-left: ${w} solid ${colour};`;
};

/** Plain CSS for the configured look — injected at runtime and reused by the
 *  settings preview, so what you see there is exactly what readers get. */
export function calloutCss(cfg) {
  const shape = { ...DEFAULT_CALLOUT_STYLES.shape, ...(cfg?.shape || {}) };
  const variants = { ...DEFAULT_CALLOUT_STYLES.variants, ...(cfg?.variants || {}) };
  const radius = (RADIUS_OPTIONS[shape.radius] || RADIUS_OPTIONS.md).css;
  const pad = shape.icons ? "0.85rem 1rem 0.85rem 2.6rem" : "0.85rem 1rem";
  const out = [`.kb-callout{border-radius:${radius};padding:${pad};}`];
  if (!shape.icons) out.push(".kb-callout::before{content:none;}");

  for (const [id, conf] of Object.entries(variants)) {
    const c = calloutColours(conf.palette);
    const ico = CALLOUT_ICONS[conf.icon] || CALLOUT_ICONS.info;
    const sel = `.kb-callout.kb-callout-${id}`;
    const head = { ...DEFAULT_HEADING, ...(conf.heading || {}) };
    // Heading rules per type — set in KB Settings, applied at render time, so
    // one change reaches every article at once.
    const hSel = `${sel} > .kb-callout-title`;
    if (head.show === false) {
      out.push(`${hSel}{display:none;}`);
    } else {
      const size = (HEADING_SIZES[head.size] || HEADING_SIZES.md).css;
      const tc = (HEADING_CASES[head.case] || HEADING_CASES.none).css;
      out.push(`${hSel}{font-size:${size};font-weight:${head.weight || 700};`
        + `text-transform:${tc};${head.colour === "match" ? `color:${c.light.icon};` : ""}}`);
      if (head.colour === "match") {
        out.push(`.dark ${hSel}{color:${c.dark.icon};}`);
      }
    }
    // Dot used by the editor's insert menu, so it matches the real colour.
    out.push(`.kb-swatch-${id}{background:${c.light.accent};}`);
    out.push(`.dark .kb-swatch-${id}{background:${c.dark.accent};}`);
    out.push(`${sel}{background:${c.light.bg};color:${c.light.text};${border(shape, c.light.accent)}}`);
    out.push(`.dark ${sel}{background:${c.dark.bg};color:${c.dark.text};${border(shape, c.dark.accent)}}`);
    if (shape.icons && ico.char) {
      out.push(`${sel}::before{content:"${ico.char}";color:${c.light.icon};`
        + `font-style:${ico.italic ? "italic" : "normal"};}`);
      out.push(`.dark ${sel}::before{color:${c.dark.icon};}`);
    } else {
      out.push(`${sel}::before{content:none;}`);
    }
  }
  return out.join("\n");
}

// The live configuration, cached once the KB stylesheet is injected, so the
// editor can pre-fill a new callout with that type's default heading.
let liveConfig = null;
export function setLiveCalloutConfig(cfg) { liveConfig = cfg || null; }
export function calloutTitleDefault(variant) {
  const saved = (liveConfig?.variants || {})[variant]?.title_default;
  if (typeof saved === "string" && saved.trim()) return saved.trim();
  return DEFAULT_CALLOUT_STYLES.variants[variant]?.title_default || "";
}
